// ADV_FORMAT on charts — the measure column's conditional formatting, the SAME
// string and the SAME evaluator the Grid uses (conditionalFormat.ts), applied
// to the marks a chart draws: bars, points, slices, funnel stages, tiles.
//
//   "#value# < #TARGET_AMT#~{\"theme\":\"alert\"}$#value# >= #TARGET_AMT#~{\"theme\":\"good\"}"
//
// - `#value#` is the mark's own value; `#NAME#` is another column of the row
//   the mark was drawn from (matched by its category — and its series when the
//   chart splits by one).
// - A rule's `theme` spreads that theme's colours over the marks it picks out,
//   IN ORDER: with three states below target, they take the theme's 1st, 2nd
//   and 3rd colours. `backgroundColor` (else `color`) gives every match one
//   colour. A mark no rule matches keeps the colour the chart gave it.
// - Matching rules merge per key, later winning — exactly as on a grid cell.
//
// Where: the one seam every vendored chart draws through — Plotly.newPlot /
// Plotly.react, wrapped by installPlotlyTheme. The chart instance behind a
// plot is found through the host element InsightVisual registers, so no chart
// class carries its own copy. Never throws: a figure it cannot read is drawn
// exactly as given.

import { parseAdvFormat, conditionalFormat, formulaColumns, type FmtCondition } from './conditionalFormat'
import { themePalette } from './visualColors'

interface MappedCol {
  fieldName?: string
  label?: string
  formatting?: unknown
  pattern?: unknown
}

interface MappingGroup { columns?: MappedCol[] }

/** What this module reads off a vendored chart instance. */
export interface ChartLike {
  columnMapping?: Record<string, MappingGroup | undefined>
  data?: Array<Record<string, unknown>>
  containerElement?: Element | null
  formatAxisValues?: (values: unknown[], col: unknown) => unknown[]
  _stripDrilldownIcon?: (label: unknown) => unknown
  getThemeColorPalette?: () => string[] | null
}

const hosts = new WeakMap<Element, ChartLike>()

/** InsightVisual: the chart instance drawing inside `el`. */
export function registerChartHost(el: Element | null | undefined, chart: unknown): void {
  if (el && chart) hosts.set(el, chart as ChartLike)
}

export function unregisterChartHost(el: Element | null | undefined): void {
  if (el) hosts.delete(el)
}

function chartFor(gd: unknown): { chart: ChartLike; el: Element } | null {
  let node: Element | null = typeof gd === 'string'
    ? (typeof document !== 'undefined' ? document.getElementById(gd) : null)
    : (gd as Element | null)
  while (node) {
    const chart = hosts.get(node)
    if (chart) return { chart, el: node }
    node = node.parentElement
  }
  return null
}

type Obj = Record<string, unknown>
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v)

const VALUE_GROUPS = ['values', 'value']
const CATEGORY_GROUPS = ['rows', 'stage', 'label', 'category', 'axis']
const SEP = String.fromCharCode(1)

function group(chart: ChartLike, names: string[]): MappedCol[] {
  const m = chart.columnMapping || {}
  for (const n of names) {
    const g = m[n] ?? m[n.toLowerCase()]
    if (g?.columns?.length) return g.columns
  }
  return []
}

interface Measure { col: MappedCol; label: string; conditions: FmtCondition[]; needsRow: boolean }

function measuresOf(chart: ChartLike): { all: MappedCol[]; ruled: Measure[] } {
  const all = group(chart, VALUE_GROUPS)
  const ruled: Measure[] = []
  for (const col of all) {
    const adv = typeof col.formatting === 'string' ? col.formatting : ''
    const conditions = parseAdvFormat(adv)
    if (!conditions.length) continue
    ruled.push({
      col,
      label: String(col.label ?? col.fieldName ?? ''),
      conditions,
      needsRow: conditions.some((c) => formulaColumns(c.formula).length > 0),
    })
  }
  return { all, ruled }
}

/** Which ruled measure a trace draws: the only measure, else by its name
 *  ("Sales", "Sum of Sales", "East — Sales"). */
function measureOfTrace(trace: Obj, allCount: number, ruled: Measure[]): Measure | null {
  if (allCount === 1) return ruled[0] ?? null
  const name = String(trace.name ?? '')
  for (const m of ruled) {
    if (!m.label) continue
    if (name === m.label || name.endsWith(` of ${m.label}`) || name.endsWith(` — ${m.label}`)) return m
  }
  return null
}

const norm = (v: unknown) => String(v ?? '').replace(/ +$/, '')

function sumRows(rows: Array<Record<string, unknown>>): Record<string, unknown> {
  if (rows.length === 1) return rows[0]
  const out: Record<string, unknown> = { ...rows[0] }
  for (const k of Object.keys(out)) {
    let total = 0
    let numeric = true
    for (const r of rows) {
      const n = typeof r[k] === 'number' ? (r[k] as number) : Number(r[k])
      if (r[k] === null || r[k] === undefined || r[k] === '' || !Number.isFinite(n)) { numeric = false; break }
      total += n
    }
    if (numeric) out[k] = total
  }
  return out
}

/** Row lookup by the label a mark shows — raw and as the chart formats it. */
function rowIndex(chart: ChartLike, catCol: MappedCol | undefined, seriesCol: MappedCol | undefined) {
  const rows = Array.isArray(chart.data) ? chart.data : []
  const byKey = new Map<string, Array<Record<string, unknown>>>()
  if (!catCol?.fieldName) return { lookup: () => null as Record<string, unknown> | null }
  const fmt = (vals: unknown[], col: MappedCol): unknown[] => {
    try {
      const out = chart.formatAxisValues?.call(chart, vals, col)
      return Array.isArray(out) ? out : vals
    } catch {
      return vals
    }
  }
  const cats = rows.map((r) => r[catCol.fieldName!])
  const catsFmt = fmt(cats, catCol)
  const sers = seriesCol?.fieldName ? rows.map((r) => r[seriesCol.fieldName!]) : null
  const sersFmt = sers && seriesCol ? fmt(sers, seriesCol) : null
  const add = (key: string, row: Record<string, unknown>) => {
    const list = byKey.get(key)
    if (list) { if (!list.includes(row)) list.push(row) } else byKey.set(key, [row])
  }
  rows.forEach((row, i) => {
    const catKeys = new Set([norm(cats[i]), norm(catsFmt[i])])
    for (const ck of catKeys) {
      add(ck, row)
      if (sers && sersFmt) for (const sk of new Set([norm(sers[i]), norm(sersFmt[i])])) add(ck + SEP + sk, row)
    }
  })
  const strip = (label: unknown) => {
    try { return chart._stripDrilldownIcon ? chart._stripDrilldownIcon.call(chart, label) : label } catch { return label }
  }
  return {
    lookup: (category: unknown, traceName: string): Record<string, unknown> | null => {
      const ck = norm(strip(category))
      if (sers) {
        // A series trace is named after its series value, or "<series> — <measure>".
        for (const sk of [norm(traceName), norm(traceName.split(' — ')[0])]) {
          const hit = byKey.get(ck + SEP + sk)
          if (hit) return sumRows(hit)
        }
      }
      const hit = byKey.get(ck)
      return hit ? sumRows(hit) : null
    },
  }
}

interface Shape { cats: unknown[]; vals: unknown[]; colorKey: 'color' | 'colors' }

function shapeOf(trace: Obj): Shape | null {
  const type = String(trace.type ?? 'scatter')
  const arr = (v: unknown) => (Array.isArray(v) ? v : null)
  if (type === 'bar' || type === 'funnel') {
    const horizontal = type === 'funnel' ? trace.orientation !== 'v' : trace.orientation === 'h'
    const cats = arr(horizontal ? trace.y : trace.x) ?? arr(trace.labels)
    const vals = arr(horizontal ? trace.x : trace.y) ?? arr(trace.values)
    return cats && vals ? { cats, vals, colorKey: 'color' } : null
  }
  if (type === 'scatter' || type === 'scattergl') {
    const cats = arr(trace.x)
    // A bubble's measure is its size, whose raw values ride on `meta` (the
    // marker sizes are rescaled); a line / scatter point's is its y.
    const meta = isObj(trace.meta) ? trace.meta : {}
    const vals = arr(meta.measureValues) ?? arr(trace.y)
    return cats && vals ? { cats, vals, colorKey: 'color' } : null
  }
  if (type === 'pie' || type === 'treemap' || type === 'funnelarea') {
    const cats = arr(trace.labels)
    const vals = arr(trace.values)
    return cats && vals ? { cats, vals, colorKey: 'colors' } : null
  }
  return null
}

/**
 * The traces with each ruled measure's marks recoloured. Traces the rules do
 * not touch are returned as the same objects; touched ones are copies.
 */
export function applyChartAdvFormat(gd: unknown, data: unknown): unknown {
  if (!Array.isArray(data) || !data.length) return data
  try {
    const found = chartFor(gd)
    if (!found) return data
    const { chart, el } = found
    const { all, ruled } = measuresOf(chart)
    if (!ruled.length) return data

    const rowCats = group(chart, CATEGORY_GROUPS)
    const colCats = group(chart, ['columns'])
    const catCol = rowCats[0] ?? colCats[0]
    const seriesCol = rowCats.length ? colCats[0] : undefined
    const index = ruled.some((m) => m.needsRow) ? rowIndex(chart, catCol, seriesCol) : null
    let basePalette: string[] | null = null
    const paletteFallback = () => {
      if (!basePalette) {
        try { basePalette = chart.getThemeColorPalette?.call(chart) ?? null } catch { basePalette = null }
        if (!basePalette || !basePalette.length) basePalette = themePalette('default', el) ?? ['#2a78d6']
      }
      return basePalette
    }
    // Per measure + theme: how many marks that theme has coloured so far.
    const counters = new Map<string, number>()
    const themeCache = new Map<string, string[] | null>()
    const themeColors = (name: string) => {
      if (!themeCache.has(name)) themeCache.set(name, themePalette(name, el))
      return themeCache.get(name) ?? null
    }

    let changed = false
    const out = data.map((t) => {
      if (!isObj(t)) return t
      const measure = measureOfTrace(t, all.length, ruled)
      if (!measure) return t
      const shape = shapeOf(t)
      if (!shape) return t
      const marker = isObj(t.marker) ? t.marker : {}
      const existing = marker[shape.colorKey]
      const traceName = String(t.name ?? '')
      let hit = false
      const colors = shape.cats.map((cat, i) => {
        const row = measure.needsRow && index ? index.lookup(cat, traceName) : null
        const format = conditionalFormat(measure.conditions, shape.vals[i], row)
        let colour: string | null = null
        if (format?.theme) {
          const pal = themeColors(String(format.theme))
          if (pal && pal.length) {
            const key = `${measure.col.fieldName ?? measure.label}${SEP}${String(format.theme).toLowerCase()}`
            const n = counters.get(key) ?? 0
            counters.set(key, n + 1)
            colour = pal[n % pal.length]
          }
        }
        if (!colour && format) colour = format.backgroundColor || format.color || null
        if (colour) { hit = true; return colour }
        if (Array.isArray(existing)) return existing[i] ?? paletteFallback()[i % paletteFallback().length]
        if (typeof existing === 'string' && existing) return existing
        return shape.colorKey === 'colors' ? paletteFallback()[i % paletteFallback().length] : paletteFallback()[0]
      })
      if (!hit) return t
      changed = true
      const next: Obj = { ...t, marker: { ...marker, [shape.colorKey]: colors } }
      // A line with no markers would hide the highlight — show the points.
      if ((t.type === 'scatter' || t.type === 'scattergl' || t.type === undefined)
        && typeof t.mode === 'string' && !t.mode.includes('markers')) {
        next.mode = `${t.mode}+markers`
      }
      return next
    })
    return changed ? out : data
  } catch (err) {
    console.warn('[visual-engine] ADV_FORMAT not applied to a chart:', err)
    return data
  }
}
